import * as React from 'react';
import PropTypes from 'prop-types';

import assertions from 'lib/assertions';
//@ts-ignore
import AuthActions from 'auth/AuthActions';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
//@ts-ignore
import Device from 'entities/Device';
import Divider from 'components/Divider';
import errorLogger from 'utils/errorLogger';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Input from 'components/Input';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
//@ts-ignore
import { redirector } from 'auth/components/SignInPage';
import { MouseEventHandler } from 'types/typescript/eventHandlers';

type Props = {
  deviceType: 'email' | 'phone';
  onGoBack: MouseEventHandler;
  uid: string;
  needsPassword: boolean;
  needsConfirmationErrorCode: string | undefined;
};

type State = {
  verifying: boolean;
  code?: string;
};

class ConfirmDevice extends React.Component<Props, State> {
  state: State = {
    verifying: false,
    code: undefined,
  };

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  componentDidMount() {
    EventTracking.trackKey(`log_in.confirm_${this.props.deviceType}_page.view`);
    this.sendSMSConf(
      () => ToastActions.showSuccessToast('Code sent'),
      () => ToastActions.showErrorToast('Error sending the code')
    );
  }

  continue = () => {
    this.setState({ verifying: true });

    const onError = () => {
      this.setState({ verifying: false });
      ToastActions.showErrorToast(
        `Error verifying your ${this.getReadableDeviceType(this.props.deviceType)}.`
      );
    };

    const onSuccess = ({ token }: { token: string }) => {
      AuthActions.accessTokenSignIn(token)
        .then(() => {
          const { attemptedTransition, onSignInRedirectTo } = redirector;

          if (attemptedTransition) {
            redirector.reset();
            this.context.router.push(attemptedTransition);
            return;
          }

          if (onSignInRedirectTo) {
            redirector.reset();
            this.context.router.push(onSignInRedirectTo);
            return;
          }

          this.context.router.push('/');
        })
        .catch(errorLogger('ConfirmDevice sign in failed'));
    };

    return Device.createOutboundDevice(this.props.uid, this.state.code).then(onSuccess, onError);
  };

  getReadableDeviceType = (device: string) => {
    if (device === 'email') {
      return 'email address';
    } else {
      return device;
    }
  };

  sendSMSConf = (onSuccess: () => void, onError: () => void) => {
    return Device.outboundVerify(this.props.uid).then(onSuccess, onError);
  };

  resendSMSConf = () => {
    const onError = () => ToastActions.showErrorToast('Error sending the code');
    const onSuccess = () => ToastActions.showSuccessToast('Code successfully resent');
    return this.sendSMSConf(onSuccess, onError);
  };

  getHeading = () => {
    const { needsPassword, needsConfirmationErrorCode, deviceType } = this.props;
    const defaultText = `Confirm your ${this.getReadableDeviceType(deviceType)}`;
    if (needsPassword) {
      return defaultText;
    } else if (needsConfirmationErrorCode) {
      const reconfirmText = 'It’s been a while since you logged in';
      const confirmText = 'This looks like a new device';
      const codeToHeadingMap: Record<string, string> = {
        unconfirmed_contactable_email: 'Check your email',
        unconfirmed_contactable_phone: 'Check your text messages',
        session_login_reconfirm_email: reconfirmText,
        session_login_reconfirm_phone: reconfirmText,
        session_login_confirm_email: confirmText,
        session_login_confirm_phone: confirmText,
      };
      return codeToHeadingMap[needsConfirmationErrorCode] ?? defaultText;
    }
    return defaultText;
  };

  getReason = () => {
    const { needsPassword, needsConfirmationErrorCode } = this.props;
    const confirmText = 'To confirm your account';
    const verifyText = 'To verify it’s you';

    if (needsPassword) {
      return 'Your account doesn’t have a password yet. To set it up';
    } else if (needsConfirmationErrorCode) {
      const codeToReasonMap: Record<string, string> = {
        unconfirmed_contactable_email: confirmText,
        unconfirmed_contactable_phone: confirmText,
        session_login_reconfirm_email: verifyText,
        session_login_reconfirm_phone: verifyText,
        session_login_confirm_email: verifyText,
        session_login_confirm_phone: verifyText,
      };
      return codeToReasonMap[needsConfirmationErrorCode] ?? confirmText;
    }
    return confirmText;
  };

  render() {
    const { deviceType, uid } = this.props;
    return (
      <div className="ConfirmDevice">
        {this.props.onGoBack && (
          <Row size="small">
            <Text align="left">
              <Link
                onClick={this.props.onGoBack}
                eventKey={`log_in.confirm_${deviceType}_page.go_back.click`}>
                <Icon name="arrow-left" />
                {` Back`}
              </Link>
            </Text>
          </Row>
        )}

        <Row size="small">
          <Heading data-test="confirm-device-heading">{this.getHeading()}</Heading>
          <Text color="thunder">
            {`${this.getReason()}, enter the 4-digit code that was just sent to `}
            <Text weight="semibold">{uid}</Text>
          </Text>
        </Row>

        <Divider size="medium" />

        <Row size="small">
          <FormElement
            label="Confirmation code"
            element={Input}
            validate={e => {
              assertions.number(e.target.value, 'Must be a 4-digit numerical code');
              assertions.lengthEqualTo(e.target.value, 4, 'Must be a 4-digit numerical code');
            }}
            onValidChange={e => this.setState({ code: e.target.value })}
            onInvalidChange={() => this.setState({ code: undefined })}
            elementProps={{
              autofocus: true,
              placeholder: '0000',
              maxLength: 4,
              size: 'large',
              'data-test': 'confirm-input-box',
            }}
          />
        </Row>
        <Row size="medium">
          <Buttons axis="y">
            <Button
              disabled={!this.state.code || this.state.verifying}
              onClick={this.continue}
              full
              data-test="submit-code-button"
              eventKey={`log_in.confirm_${deviceType}_page.submit.click`}>
              Submit
            </Button>
          </Buttons>
        </Row>

        <Divider size="medium" />

        <Row size="xxsmall">
          <Text color="thunder">
            Didn’t get a code?{' '}
            <Link
              data-test="resend-sms-conf-link"
              onClick={this.resendSMSConf}
              eventKey={`log_in.confirm_${deviceType}_page.resend.click`}>
              Resend
            </Link>{' '}
            or{' '}
            <Link
              href={LinkConstants.CONTACT_HELP}
              target="_blank"
              eventKey={`log_in.confirm_${deviceType}_page.contact_support.click`}>
              contact Support
            </Link>
          </Text>
        </Row>
      </div>
    );
  }
}

export default ConfirmDevice;
